import { useCallback } from 'react';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';

import { ApiError, formFieldPath } from '@nucleus-commerce/api-client/transport';

/**
 * Pushes API field errors into react-hook-form. The backend prefixes every path with its
 * source ("body.variants.0.sku", "params.id"); the body prefix is stripped so the path lines
 * up with the form, and paths not present in the form land on `root.server` so nothing is
 * silently lost. Returns true when at least one field error was applied.
 */
export function applyFieldErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  options: { knownFields?: ReadonlySet<string>; mapField?: (apiField: string) => string } = {},
): boolean {
  if (!(error instanceof ApiError) || !error.hasFieldErrors) return false;
  const { knownFields, mapField = (f) => f } = options;
  let applied = false;
  for (const detail of error.details) {
    const field = mapField(formFieldPath(detail.field));
    const target = knownFields && !knownFields.has(field) ? 'root.server' : field;
    setError(target as Path<T> | `root.${string}`, { type: detail.code, message: detail.message });
    applied = true;
  }
  return applied;
}

export function useApplyFieldErrors<T extends FieldValues>(
  setError: UseFormSetError<T>,
  mapField?: (apiField: string) => string,
) {
  return useCallback(
    (error: unknown) => applyFieldErrors(error, setError, mapField ? { mapField } : {}),
    [setError, mapField],
  );
}
